import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Chip } from '../../components/ui/chip'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'
import { errorCode, errorReason, sessionGone } from '../../lib/errors'
import { EmptyState, ErrorState, Loading, Problem } from '../components/chrome'
import { CompanyDataSection } from '../components/company-data'
import { CompanyNoaSection } from '../components/company-noa'
import {
  CompanyCounts,
  CompanyInitials,
  OrganizationStatus,
  ProgramStatus,
} from '../components/company-parts'
import { HrTeamSection } from '../components/hr-team'
import { PasskeyNote } from '../components/passkey-note'
import { useReauth } from '../components/reauth'
import { forgetCreatedCompany, isJustCreated } from '../lib/created-company'
import { useI18n } from '../lib/i18n'
import {
  companyGone,
  companyKey,
  isCompanyId,
  organizationChanged,
  useCompanyDetail,
  type CompanyDetail,
  type CompanyStatus,
  type Reopen,
} from '../lib/screens'
import { forCompany, useTRPCClient } from '../lib/trpc'

/*
 * A company's detail (PRD 04 §2.2, ADR 0008 §3; Figma 86:2908, not verified): opening it makes
 * the company the session's working organization and leaves `operator.org_opened` in that
 * company's audit trail, once per view. Its name, state and counts; its details (language, time
 * zone, brand) with «Editar» (ADR 0009 §8); its HR team, the only people the panel shows, with
 * «Reenviar invitación» (ADR 0009 §8); and the courses of Eklevo it has activated with their
 * program's state; and Noa's state (ADR 0010 §8 and §9). «Suspender» or «Reactivar», after a
 * confirmation that says what it does and the passkey again (ADR 0008 §4), leave their entry in
 * the same trail. Without the Figma's sector, tax id, address, Noa block and «Dar de baja», which
 * are not built (ADR 0009 §8). Where the
 * company wizard leads once it has created the company (ADR 0009 §7), the page says so first,
 * once. Marked for design review.
 */
export function CompanyPage() {
  const { organizationId } = useParams({ from: '/_shell/empresas/$organizationId' })
  const detail = useCompanyDetail(organizationId)
  const [created] = useState(() => isJustCreated(organizationId))
  useEffect(() => forgetCreatedCompany(), [])
  if (!isCompanyId(organizationId) || (detail.isError && companyGone(detail.error)))
    return <CompanyGone />
  if (detail.isPending) return <Loading />
  if (detail.isError) return <ErrorState onRetry={() => void detail.refetch()} />
  return <CompanyView company={detail.data} created={created} reopen={() => detail.refetch()} />
}

function BackToCompanies() {
  const { t } = useI18n()
  return (
    <Link
      to="/empresas"
      className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
    >
      <ArrowLeft aria-hidden className="size-4" />
      {t('company.back')}
    </Link>
  )
}

function CompanyGone() {
  const { t } = useI18n()
  return (
    <div className="space-y-6">
      <BackToCompanies />
      <Card>
        <h1 className="text-2xl">{t('company.notFound.title')}</h1>
        <p className="mt-3 text-ink-muted">{t('company.notFound.body')}</p>
      </Card>
    </div>
  )
}

/** What the page says once «Suspender» or «Reactivar» is done, or why it could not be. */
interface Notice {
  tone: 'done' | 'error'
  text: string
  /** Another tab opened another company: the way to open this one again. */
  reopen?: boolean
}

function CompanyView({
  company,
  created,
  reopen,
}: {
  company: CompanyDetail
  /** The wizard has just created it: the page says so first, with the focus. */
  created: boolean
  reopen: Reopen
}) {
  const { t } = useI18n()
  const coursesId = useId()
  const countsId = useId()
  const { action, notice, dialog } = useStatusChange(company)
  const createdNotice = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (created) createdNotice.current?.focus()
  }, [created])
  return (
    <div className="space-y-6">
      <BackToCompanies />
      {created && (
        <p
          ref={createdNotice}
          tabIndex={-1}
          className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success outline-none"
        >
          {t('company.created', { count: company.courses.length })}
        </p>
      )}
      <div className="flex flex-wrap items-start gap-4">
        <CompanyInitials
          name={company.name}
          className="size-14 border border-line bg-surface text-base"
        />
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl [overflow-wrap:anywhere]">{company.name}</h1>
          <div className="mt-2">
            <OrganizationStatus status={company.status} />
          </div>
        </div>
        <div className="w-full sm:w-auto">{action}</div>
      </div>

      {/* The two messages are always on the page, so that they are read out as they change. */}
      <div>
        <p
          role="status"
          className="text-sm text-success not-empty:rounded-lg not-empty:bg-success/10 not-empty:px-4 not-empty:py-3"
        >
          {notice?.tone === 'done' ? notice.text : ''}
        </p>
        <Problem>{notice?.tone === 'error' ? notice.text : ''}</Problem>
        {notice?.reopen && (
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => void reopen()}>
            {t('company.reopen')}
          </Button>
        )}
      </div>

      <section aria-labelledby={countsId}>
        <Card>
          <h2 id={countsId} className="text-lg font-medium">
            {t('company.counts')}
          </h2>
          <CompanyCounts company={company} className="mt-4" />
          <p className="mt-4 text-xs text-ink-muted">{t('company.activeCoursesHint')}</p>
        </Card>
      </section>

      <CompanyDataSection company={company} reopen={reopen} />

      <CompanyNoaSection noa={company.noa} />

      <HrTeamSection company={company} reopen={reopen} />

      <section aria-labelledby={coursesId} className="space-y-4">
        <h2 id={coursesId} className="text-xl">
          {t('company.courses.title')}
        </h2>
        {company.courses.length === 0 ? (
          <EmptyState title={t('company.courses.empty')}>
            <Link to="/cursos" className="underline hover:text-ink">
              {t('company.courses.toCatalog')}
            </Link>
          </EmptyState>
        ) : (
          <ul className="divide-y divide-line rounded-card border border-line bg-surface">
            {company.courses.map((c) => (
              <li key={c.courseId} className="flex flex-wrap items-center gap-3 px-5 py-4">
                <span className="min-w-0 flex-1 font-medium [overflow-wrap:anywhere]">
                  {c.title}
                </span>
                {c.level !== null && <Chip>{t('catalog.level', { level: c.level })}</Chip>}
                <ProgramStatus status={c.status} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-xs text-ink-muted">{t('company.audited')}</p>
      {dialog}
    </div>
  )
}

/**
 * «Suspender» an active company or «Reactivar» a suspended one: a confirmation that says what
 * it does, then the passkey again if it was not touched in the last minutes (the reauthentication
 * dialog, once the confirmation has closed and the focus is back on the button), then the call,
 * with the company the page shows as the one it expects. The button stays where it was, with
 * the other action's name once done.
 */
function useStatusChange(company: CompanyDetail) {
  const { t } = useI18n()
  const client = useTRPCClient()
  const queryClient = useQueryClient()
  const reauth = useReauth()
  const button = useRef<HTMLButtonElement>(null)
  const [confirming, setConfirming] = useState(false)
  const confirmed = useRef(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  const to: CompanyStatus = company.status === 'active' ? 'suspended' : 'active'

  const showStatus = (status: CompanyStatus) =>
    queryClient.setQueryData<CompanyDetail>(companyKey(company.id), (d) => d && { ...d, status })

  const change = useMutation({
    mutationFn: (target: CompanyStatus) =>
      reauth.run(
        () =>
          target === 'suspended'
            ? client.companies.suspend.mutate(undefined, forCompany(company.id))
            : client.companies.reactivate.mutate(undefined, forCompany(company.id)),
        { from: button.current },
      ),
    onSuccess: (r, target) => {
      // Cancelled at the passkey: nothing done, nothing to say.
      if (!r) return
      showStatus(r.status)
      setNotice({
        tone: 'done',
        text: t(target === 'suspended' ? 'company.suspended' : 'company.reactivated'),
      })
    },
    onError: (err, target) => {
      if (sessionGone(err)) return
      const reason = errorReason(err)
      if (reason === 'status-unchanged') {
        // Someone else did it meanwhile: the company is already as asked.
        showStatus(target)
        setNotice({ tone: 'error', text: t('company.error.unchanged') })
      } else if (organizationChanged(err))
        setNotice({ tone: 'error', text: t('common.error.organizationChanged'), reopen: true })
      else if (companyGone(err)) setNotice({ tone: 'error', text: t('company.notFound.title') })
      else if (errorCode(err) === 'TOO_MANY_REQUESTS')
        setNotice({ tone: 'error', text: t('common.error.rateLimited') })
      else setNotice({ tone: 'error', text: t('common.error.generic') })
    },
  })

  const suspend = to === 'suspended'
  const action = (
    <Button
      ref={button}
      variant={suspend ? 'danger' : 'primary'}
      aria-disabled={change.isPending || undefined}
      className="aria-disabled:cursor-progress aria-disabled:opacity-50"
      onClick={() => {
        if (change.isPending) return
        setNotice(null)
        setConfirming(true)
      }}
    >
      {change.isPending
        ? t(suspend ? 'company.suspend.busy' : 'company.reactivate.busy')
        : t(suspend ? 'company.suspend' : 'company.reactivate')}
    </Button>
  )

  const dialog = (
    <ConfirmDialog
      open={confirming}
      onOpenChange={setConfirming}
      title={t(suspend ? 'company.suspend.title' : 'company.reactivate.title', {
        company: company.name,
      })}
      confirmLabel={t(suspend ? 'company.suspend' : 'company.reactivate')}
      cancelLabel={t('common.cancel')}
      danger={suspend}
      onConfirm={() => {
        confirmed.current = true
        setConfirming(false)
      }}
      opener={button}
      onClosed={() => {
        // Once the focus is back on the button: the passkey's dialog may take it.
        if (!confirmed.current) return
        confirmed.current = false
        change.mutate(to)
      }}
    >
      <p>{t(suspend ? 'company.suspend.body' : 'company.reactivate.body')}</p>
      <PasskeyNote />
    </ConfirmDialog>
  )

  return { action, notice, dialog }
}
